import React, { FC, useCallback, useMemo } from "react";
import { Flex, Menu, Popup } from "@evergis/ui";
import { ResourceTypeLink } from "@evergis/api";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getLocationQuery } from "../../../../ducks/location/selectors";
import { createTemporaryProject } from "../../../../ducks/project/project";

import { useAddTmsDialog, useImportDialog, useNewLayerDialog } from "../../../../hooks/useDialogs";
import { ViewType } from "../ViewType/ViewType";
import { NewProjectButton } from "./styled";

import { useResourceType } from "../../../../hooks/useResourceType";
import { useLimitsExceededCheck } from "../../../../hooks/useLimitsExceededCheck";
import { useToggle } from "../../../../hooks/useToggle";
import { useCatalogModule } from "../../../../hooks/appConfig/useAppModule";

interface IOption {
  text: string;
  value: string;
  action: VoidFunction;
}

// eslint-disable-next-line max-lines-per-function
export const ActionButton: FC = () => {
  const { t } = useTranslation("catalog");
  const dispatch = useDispatch();
  const [resourceType] = useResourceType();
  const [popupVisible, togglePopup] = useToggle(false);
  const { addAsBasemap } = useSelector(getLocationQuery);

  const { options: appConfigOptions } = useCatalogModule();
  const { allowCreateProxyService } = appConfigOptions || {};

  const processedImport = useLimitsExceededCheck(useImportDialog(), true);
  const processedCreateLayer = useLimitsExceededCheck(useNewLayerDialog(), true);
  const processedAddTmsService = useLimitsExceededCheck(useAddTmsDialog(), true);

  const cardLabels: Record<ResourceTypeLink, { title: string; label: string }> = useMemo(
    () => ({
      [ResourceTypeLink.Project]: {
        title: t("createNewMap"),
        label: t("newMap"),
      },
      [ResourceTypeLink.Layer]: {
        title: t("createNewLayer"),
        label: t("newLayer"),
      },
      [ResourceTypeLink.Table]: {
        title: t("createNewTable"),
        label: t("newTable"),
      },
    }),
    [t],
  );

  const { title, label } = cardLabels[resourceType];

  const labelOptions: IOption[] = useMemo(
    () => [
      {
        text: t("uploadData"),
        value: "downloadData",
        action: () => processedImport([null, ResourceTypeLink.Layer]),
      },
      {
        text: t("createEmptyLayer"),
        value: "createNew",
        action: () => processedCreateLayer([null, ResourceTypeLink.Layer]),
      },
    ],
    [processedCreateLayer, processedImport, t],
  );

  const projectOptions: IOption[] = useMemo(
    () => [
      {
        text: t("createNewMap"),
        value: "createNewMap",
        action: () => dispatch(createTemporaryProject()),
      },
      {
        text: t("importGdb"),
        value: "importGdb",
        action: () => processedImport([null, ResourceTypeLink.Layer]),
      },
    ],
    [dispatch, processedImport, t],
  );

  const selectOptions = useCallback(
    ([{ action }]: IOption[]) => {
      action();
      togglePopup();
    },
    [togglePopup],
  );

  const menuOptions = useCallback(
    (type: ResourceTypeLink) => {
      const isLayer = type === ResourceTypeLink.Layer;

      const createProxyServiceOption = {
        text: t("addTmsService"),
        value: "addTmsService",
        action: () => processedAddTmsService([addAsBasemap, ResourceTypeLink.Layer]),
      };

      return isLayer
        ? allowCreateProxyService
          ? [...labelOptions, createProxyServiceOption]
          : labelOptions
        : projectOptions;
    },
    [allowCreateProxyService, addAsBasemap, t, processedAddTmsService, labelOptions, projectOptions],
  );

  return (
    <Flex style={{ flexWrap: "nowrap", marginLeft: "1rem" }}>
      {[ResourceTypeLink.Layer, ResourceTypeLink.Project].includes(resourceType) && (
        <Popup
          isVisible={popupVisible}
          onRequestClose={togglePopup}
          anchor={ref => (
            <NewProjectButton ref={ref} title={title} primary onClick={togglePopup}>
              {label}
            </NewProjectButton>
          )}
        >
          <Menu options={menuOptions(resourceType)} selectOption={selectOptions} />
        </Popup>
      )}
      {/* {resourceType === ResourceTypeLink.Project && (
        <NewProjectButton
          title={title}
          primary
          disabled={false}
          onClick={() => processedCreateProject([null, ResourceTypeLink.Project])}
        >
          {label}
        </NewProjectButton>
      )}*/}
      <ViewType />
    </Flex>
  );
};
